import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller, useWatch, FormProvider } from "react-hook-form";
import dayjs from "dayjs";
import {
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grow,
  Collapse,
  Box,
  Stepper,
  Step,
  StepLabel,
  SelectChangeEvent,
} from "@mui/material";
import { PageLayout } from "../../ui-components/PageLayout";

import FinancialDetails from "../../ui-components/Transaction/FinancialDetails";
import {
  ICreateTransactionModel,
  IFinancialDtoModel,
  ITransactionFormData,
  DuplicateActionEnumModel,
  ICreatedEntityModel,
} from "../../types/types";
import BasicTransaction from "../../ui-components/Transaction/BasicTransaction";
import { AppDispatch } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getTransactionById,
  upsertTransactionDirect,
  upsertFinancials,
} from "../../store/modules/transactions/transactionActions";
import { RootState } from "../../store/reducers";
import LoadingDialog from "../../ui-components/Dialogs/LoadingDialog";
import DuplicateTransactionDialog from "../../ui-components/Dialogs/DuplicateTransactionDialog";
import { useParams } from "react-router-dom";
import { mergeCreatedEntities } from "../../store/modules/common/commonStateActions";
import { getUserCustomData } from "../../store/modules/common/commonActions";
import {
  PIPELINE_ROUTE,
  TransactionStatus,
  isPipelineStatus,
} from "../../hooks/usePipeline";
import { mapFinancialsToFormValues } from "../../utils/transactionFinancials";
import { getApiErrorMessage } from "../../utils/common";

const TransactionAdd = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { years } = useSelector((state: RootState) => state.transactionYear);

  const { year, transactionId } = useParams<{
    year?: string;
    transactionId?: string;
  }>();

  // "/transactions/add/pipeline" creates a Lead in the Pipeline folder
  const isPipelineFolder = year === PIPELINE_ROUTE;
  const yearId = years?.find((y) => y.year === Number(year))?.id;
  // Stable reference: `values` below is deep-compared on every render
  const [today] = useState(() => dayjs().startOf("day"));

  const [selectedYearId, setSelectedYearId] = useState<number | undefined>(
    yearId !== undefined ? Number(yearId) : undefined,
  );
  const [pendingCreatedEntities, setPendingCreatedEntities] =
    useState<ICreatedEntityModel | null>(null);

  const [loading, setLoading] = useState(false);
  const {
    loaded: userDataLoaded,
    loading: userDataLoading,
    data: userData,
  } = useSelector((state: RootState) => state.commonData.userData);

  const selectedYear = selectedYearId
    ? years.filter((x) => x.id === selectedYearId)[0]
    : undefined;

  const methods = useForm<ITransactionFormData>({
    values: {
      yearId: selectedYearId ?? null,
      status: isPipelineFolder
        ? TransactionStatus.Lead
        : TransactionStatus.Transaction,
      leadReceivedDate: isPipelineFolder ? today : null,
      transactionType: null,
      address: null,
      latitude: null,
      longitude: null,
      date: null,
      price: null,
      source: null,
      soiReferrers: null,
      agentReferrers: null,
      vendorReferrers: [],
      vendors: [],
      agentReferrered: null,
      referralOutTypeId: null,
      clients: null,
      notes: null,
      reason: null,
      area: null,
      specialGroups: null,
      specialTags: null,
      coAgentMember: null,
      coAgentMember2: null,
      commissionPercentage: null,
      grossCommissionIncome: null,
      clientRebatePrice: null,
      clientRebateGCI: null,
      clientRebateFlatAmount: null,
      referralFeePaid: null,
      referralFeeReceived: null,
      coAgentSplit: null,
      coAgentSplit2: null,
      brokerageSplit: 0,
      additionalCharges: 0,
      brkAnniversary: null,
      brkFlatFees: 0,
      flatCommission: 0,
      incentivesPaid: null,
      netCommissionIncome: null,
      netIncome: null,
      netIncomePercentage: null,
    },
    mode: "all",
  });

  const [activeStep, setActiveStep] = useState(0);
  const { control, handleSubmit, formState, getValues, trigger } = methods;

  useEffect(() => {
    if (pendingCreatedEntities) {
      dispatch(mergeCreatedEntities(pendingCreatedEntities));
      setPendingCreatedEntities(null);
    }
  }, [pendingCreatedEntities, dispatch]);

  // useEffect(() => {
  //   const subscription = methods.watch((value, { name, type }) => {
  //     if (type === "change") {
  //       // console.log(
  //       //   `Field ${name} changed to:`,
  //       //   value[name as keyof typeof value]
  //       // );
  //       // Access formState to ensure React Hook Form updates the dirty state
  //       const isDirty = methods.formState.isDirty;
  //       // console.log("Form is now dirty:", isDirty);
  //     }
  //   });

  //   return () => subscription.unsubscribe();
  // }, [methods]);
  const [createdTransactionId, setCreatedTransactionId] = useState<
    number | null
  >(null);
  const [duplicateTransactionId, setDuplicateTransactionId] = useState<
    number | null
  >(null);
  // const [forceDuplicate, setForceDuplicate] = useState<boolean>(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const navigate = useNavigate();

  // useQuery({
  //   queryKey: ["userCustomDataadd"],
  //   queryFn: () => {
  //     // if (!userDataLoaded) {
  //     dispatch(getUserCustomData());

  //     // }
  //     return Promise.resolve();
  //   },
  //   refetchOnWindowFocus: false,

  //   enabled: false,
  // });

  // The entry whose saved financials were already loaded, so going Back and Next
  // again keeps what the user has edited on the Financials step
  const financialsLoadedForIdRef = useRef<number | null>(null);

  // Creating a Closed entry applies the brokerage anniversary defaults on the server (with the
  // cap running totals); show those values instead of the blank defaults.
  // Pipeline entries (Lead/Active/Under Contract) are prefilled by FinancialDetails, the server skips them.
  const loadSavedFinancials = async (id: number, status?: number) => {
    if (
      status !== TransactionStatus.Transaction ||
      financialsLoadedForIdRef.current === id
    )
      return;
    try {
      const result = await getTransactionById(id);
      const financials = result?.transactionDetail?.financials;
      if (!financials) return;
      financialsLoadedForIdRef.current = id;
      const values = mapFinancialsToFormValues(financials);
      Object.entries(values).forEach(([key, value]) =>
        methods.setValue(key as keyof ITransactionFormData, value as never, {
          shouldDirty: false,
        }),
      );
    } catch (err) {
      console.error("Failed to load saved financials:", err);
    }
  };

  // No success dialog: open the new entry in view mode, which shows the message
  const openCreatedEntry = (id: number, status?: number) =>
    navigate(`/transactions/view/${id}`, {
      state: {
        successMessage: isPipelineStatus(status)
          ? "Pipeline Entry Created Successfully"
          : "Transaction Created Successfully",
      },
    });

  const handleSave = async (
    force: boolean = false,
    replace: boolean = false,
  ) => {
    await createTransaction(force, replace, { openView: true });
  };

  const handleNext = async () => {
    // console.log("Form dirty state:", formState.isDirty);
    await createTransaction(false, false, { moveToNextStep: true });
  };
  const handleYearChange = (event: SelectChangeEvent) => {
    if (event.target.value) setSelectedYearId(Number(event.target.value));
    else {
      setSelectedYearId(undefined);
    }
  };
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDuplicate = () => {
    if (duplicateTransactionId) {
      window.open(`/transactions/view/${duplicateTransactionId}`, "_blank");
    }
    // setShowDuplicateDialog(false);
  };

  const handleProceedAnyway = (action: DuplicateActionEnumModel) => {
    // setForceDuplicate(true);
    setShowDuplicateDialog(false);
    const actionValue = typeof action === "string" ? parseInt(action) : action;

    switch (actionValue) {
      case DuplicateActionEnumModel.Skip:
        // close dialog
        break;
      case DuplicateActionEnumModel.Overwrite:
        handleSave(true, true);
        break;
      case DuplicateActionEnumModel.AddAsNew:
        console.log("Adding as new transaction");
        handleSave(true);
        break;
      default:
        handleSave(true);
        break;
    }
  };

  const createTransaction = async (
    force: boolean = false,
    replace: boolean = false,
    options: {
      moveToNextStep?: boolean;
      openView?: boolean;
    } = {},
  ) => {
    methods.clearErrors("root");
    const isValid = await trigger([
      "status",
      "transactionType",
      "price",
      "date",
      "leadReceivedDate",
      "address",
      "clients",
    ]);
    if (!isValid) return null;
    try {
      setLoading(true);
      const data = getValues();
      const transactionData = {
        transactionId: createdTransactionId,
        status: data.status ?? TransactionStatus.Transaction,
        leadReceivedDate: data.leadReceivedDate ?? undefined,
        date: data.date,
        price: data.price,
        typeId: data.transactionType,
        notes: data.notes,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        sourceId: data.source,
        reasonId: data.reason,
        areaId: (data.area?.id ?? 0) > 0 ? data.area?.id : undefined,
        insertedArea: (data.area?.id ?? 0) > 0 ? undefined : data.area?.name,
        coAgentId:
          (data.coAgentMember?.id ?? 0) > 0
            ? data.coAgentMember?.id
            : undefined,
        insertedCoAgent:
          (data.coAgentMember?.id ?? 0) <= 0
            ? data.coAgentMember?.name
            : undefined,
        coAgent2Id:
          (data.coAgentMember2?.id ?? 0) > 0
            ? data.coAgentMember2?.id
            : undefined,
        insertedCoAgent2:
          (data.coAgentMember2?.id ?? 0) <= 0
            ? data.coAgentMember2?.name
            : undefined,
        selectedClients: data.clients
          ?.filter((f) => f.id! > 0)
          .map((client) => client.id),
        insertedClients: data.clients
          ?.filter((f) => f.id! <= 0)
          .map((client) => client.name),
        selectedGroups: data.specialGroups
          ?.filter((f) => f.id! > 0)
          .map((group) => group.id),
        insertedGroups: [
          ...new Set(
            data.specialGroups
              ?.filter((f) => f.id! <= 0)
              .map((group) => group.name),
          ),
        ],
        selectedTags: data.specialTags
          ?.filter((f) => f.id! > 0)
          .map((tag) => tag.id),
        insertedTags: [
          ...new Set(
            data.specialTags?.filter((f) => f.id! <= 0).map((tag) => tag.name),
          ),
        ],
        selectedSoiReferrers: data.soiReferrers
          ?.filter((f) => f.id! > 0)
          .map((referrer) => referrer.id),
        insertedSoiReferrers: data.soiReferrers
          ?.filter((f) => f.id! <= 0)
          .map((referrer) => referrer.name),
        selectedAgentReferrers: data.agentReferrers
          ?.filter((f) => f.id! > 0)
          .map((referrer) => referrer.id),
        insertedAgentReferrers: data.agentReferrers
          ?.filter((f) => f.id! <= 0)
          .map((referrer) => referrer.name),
        selectedVendorReferrers: data.vendorReferrers
          ?.filter((f) => f.id! > 0)
          .map((referrer) => referrer.id),
        insertedVendorReferrers: data.vendorReferrers
          ?.filter((f) => f.id! <= 0)
          .map((referrer) => referrer.name),
        selectedVendors: data.vendors
          ?.filter((f) => f.id! > 0)
          .map((vendor) => vendor.id),
        insertedVendors: data.vendors
          ?.filter((f) => f.id! <= 0)
          .map((vendor) => vendor.name),
        selectedAgentReferredOut: data.agentReferrered
          ?.filter((f) => f.id! > 0)
          .map((referrer) => referrer.id),
        insertedAgentReferredOut: data.agentReferrered
          ?.filter((f) => f.id! <= 0)
          .map((referrer) => referrer.name),
        referralOutTypeId: data.referralOutTypeId,
        forceDuplicate: force,
        replace: replace,
      } as ICreateTransactionModel;

      const result = await upsertTransactionDirect(transactionData);
      if (result.duplicateId) {
        setDuplicateTransactionId(result.duplicateId);
        setShowDuplicateDialog(true);
        return result;
      } else if (result.id) {
        if (result.createdEntities) {
          setPendingCreatedEntities(
            result.createdEntities.toJSON() as ICreatedEntityModel,
          );
        }
        setCreatedTransactionId(result.id);

        if (options.moveToNextStep) {
          await loadSavedFinancials(result.id, transactionData.status);
          setActiveStep((prev) => prev + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }

        if (options.openView) {
          openCreatedEntry(result.id, transactionData.status);
        }

        return result;
      }

      return null;
    } catch (err) {
      dispatch(getUserCustomData());

      console.error("Transaction creation failed:", err);
      // Server-side rejections (e.g. the Lead Received Date rule) go in the errors box
      methods.setError("root", {
        type: "server",
        message: getApiErrorMessage(
          err,
          "Failed to save transaction. Please try again.",
        ),
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ITransactionFormData) => {
    try {
      setLoading(true);

      if (activeStep === 0) {
        await createTransaction(false, false, { openView: true });
      } else {
        if (createdTransactionId) {
          // const financialData = {
          //   commissionPercentage: data.commissionPercentage,
          //   flatCommissionAmount: data.flatCommission,
          //   clientRebatePercentage: data.clientRebatePrice,
          //   clientRebateGciPercentage: data.clientRebateGCI,
          //   clientRebateFlatAmount: data.clientRebateFlatAmount,
          //   referralFeePaidPercentage: data.referralFeePaid,
          //   referralFeeReceivedPercentage: data.referralFeeReceived,
          //   coAgentSplitPaidPercentage: data.coAgentSplit,
          //   brokerageSplitPercentage: data.brokerageSplit,
          //   brokerageAdditionalChargePercentage: data.additionalCharges,
          //   brokerageFlatFees: data.brkFlatFees,
          //   incentivesAmount: data.incentivesPaid,
          //   brokerageAnniversaryDate: data.brkAnniversary,
          //   date: data.date,
          // } as IFinancialDtoModel;
          const financialData = {
            commissionPercentage:
              data.commissionPercentage?.toString().trim() === ""
                ? null
                : data.commissionPercentage,
            flatCommissionAmount:
              data.flatCommission?.toString().trim() === ""
                ? null
                : data.flatCommission,
            clientRebatePercentage:
              data.clientRebatePrice?.toString().trim() === ""
                ? null
                : data.clientRebatePrice,
            clientRebateGciPercentage:
              data.clientRebateGCI?.toString().trim() === ""
                ? null
                : data.clientRebateGCI,
            clientRebateFlatAmount:
              data.clientRebateFlatAmount?.toString().trim() === ""
                ? null
                : data.clientRebateFlatAmount,
            referralFeePaidPercentage:
              data.referralFeePaid?.toString().trim() === ""
                ? null
                : data.referralFeePaid,
            referralFeeReceivedPercentage:
              data.referralFeeReceived?.toString().trim() === ""
                ? null
                : data.referralFeeReceived,
            coAgentSplitPaidPercentage:
              data.coAgentSplit?.toString().trim() === ""
                ? null
                : data.coAgentSplit,
            coAgentSplit2PaidPercentage:
              data.coAgentSplit2?.toString().trim() === ""
                ? null
                : data.coAgentSplit2,
            brokerageSplitPercentage:
              data.brokerageSplit?.toString().trim() === ""
                ? null
                : data.brokerageSplit,
            brokerageAdditionalChargePercentage:
              data.additionalCharges?.toString().trim() === ""
                ? null
                : data.additionalCharges,
            brokerageFlatFees:
              data.brkFlatFees?.toString().trim() === ""
                ? null
                : data.brkFlatFees,
            incentivesAmount:
              data.incentivesPaid?.toString().trim() === ""
                ? null
                : data.incentivesPaid,
            brokerageAnniversaryDate:
              data.brkAnniversary?.toString().trim() === ""
                ? null
                : data.brkAnniversary,
            date: data.date,
          } as IFinancialDtoModel;

          await upsertFinancials(createdTransactionId, financialData);
          openCreatedEntry(createdTransactionId, data.status);
        }
      }
    } catch (err) {
      console.error("Transaction operation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <LoadingDialog open={loading || userDataLoading} />
      <DuplicateTransactionDialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        onViewDuplicate={handleViewDuplicate}
        onProceed={handleProceedAnyway}
        duplicateId={duplicateTransactionId}
      />
      <Box sx={{ mb: 6, ml: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
          >
            {isPipelineFolder ? "New Pipeline Entry" : "New Closed Transaction"}
          </Typography>
          <Box
            sx={{
              display: isPipelineFolder ? "none" : "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Year:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedYearId?.toString() ?? ""}
                onChange={handleYearChange}
                variant="outlined"
              >
                {years.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <FormProvider {...methods}>
          <Grid container spacing={3} padding={3}>
            <Grid item xs={12} pb={2}>
              <Stepper activeStep={activeStep} sx={{ mt: 3 }}>
                <Step>
                  <StepLabel>Transaction Details</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Financial Details</StepLabel>
                </Step>
              </Stepper>
            </Grid>

            <>
              <Collapse in={activeStep === 0} sx={{ width: "100%" }}>
                <Card sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Transaction Details
                  </Typography>
                  <Grid container spacing={3} minHeight={400}>
                    <Grid item xs={12}>
                      <BasicTransaction
                        year={selectedYear?.year ?? 0}
                        isCreate={true}
                        isPipelineFolder={isPipelineFolder}
                      />
                    </Grid>
                    {/* <Grid item xs={12} sx={{ textAlign: "right" }}>
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          backgroundColor: "primary.main",
                          "&:hover": { backgroundColor: "primary.dark" },
                          textTransform: "none",
                          px: 4,
                          py: 1.5,
                          minWidth: "160px",
                        }}
                        onClick={handleNext}
                      >
                        Next
                      </Button>
                    </Grid> */}
                  </Grid>
                </Card>
              </Collapse>

              <Collapse in={activeStep === 1} sx={{ width: "100%" }}>
                <Card sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Financial Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FinancialDetails
                        isFinancialTab={activeStep === 1}
                        transactionId={createdTransactionId}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Collapse>
            </>
          </Grid>
          <Grid item xs={12}>
            {Object.keys(formState.errors).length > 0 && (
              <Box
                sx={{
                  width: "100%",
                  mb: 2,
                  p: 2,
                  backgroundColor: "#ffeeee",
                  border: "1px solid #ffcccc",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" color="error" gutterBottom>
                  Please fix these errors:
                </Typography>
                {Object.entries(formState.errors).map(
                  ([fieldName, error], index) => (
                    <Typography key={index} variant="body2" color="error">
                      • {error?.message || `Invalid value in ${fieldName}`}
                    </Typography>
                  ),
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "right", mt: 3 }}>
            {activeStep === 1 && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleBack}
                sx={{
                  mr: 2,
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  minWidth: "160px",
                }}
              >
                Back
              </Button>
            )}
            {activeStep === 0 && (
              <Button
                variant="contained"
                size="large"
                onClick={() => handleSave(false)}
                sx={{
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "primary.dark" },
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  minWidth: "160px",
                  mr: 2,
                }}
              >
                Save
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={activeStep === 0 ? handleNext : handleSubmit(onSubmit)}
              sx={{
                backgroundColor: "primary.main",
                "&:hover": { backgroundColor: "primary.dark" },
                textTransform: "none",
                px: 4,
                py: 1.5,
                minWidth: "160px",
              }}
            >
              {activeStep === 0 ? "Go to Financials" : "Submit"}
            </Button>
          </Grid>
        </FormProvider>
      </Box>
    </PageLayout>
  );
};

export default TransactionAdd;
